import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, Play, Check, AlertTriangle, RefreshCw, Zap, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * EnrichFactsCard — admin tile on /admin.
 *
 * Two ways to fill the filter gaps (dog_policy, family_friendly,
 * pitch_surface, campfires, open_year_round):
 *
 * 1. **Always-on auto-loop** (iter130) — flips a toggle in db.system_flags
 *    and a background asyncio task runs 24/7 through the catalogue,
 *    surviving pod restarts. Best for "leave it running all day".
 *
 * 2. **Manual batch** — legacy one-off runs via FastAPI BackgroundTasks.
 *    Handy for testing but can be killed by hot reload / restarts.
 *
 * ≈ £0.001 per site · full catalogue ≈ £20. Safe to stop any time.
 */
export default function EnrichFactsCard() {
  const [needed, setNeeded] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batchSize, setBatchSize] = useState(25);
  const [autoStatus, setAutoStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: needData }, { data: autoData }] = await Promise.all([
        api.get("/admin/enrich-facts/needed"),
        api.get("/admin/enrich-facts/auto"),
      ]);
      setNeeded(needData.needed);
      setLastRun(needData.last_run || null);
      setAutoStatus(autoData);
    } catch (e) {
      toast.error("Could not load enrichment status");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while a manual run is in flight OR the always-on loop is enabled
  useEffect(() => {
    const running = (lastRun && lastRun.status === "running") || (autoStatus && autoStatus.enabled);
    if (!running) return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [lastRun, autoStatus, load]);

  const toggleAuto = async (nextEnabled) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/enrich-facts/auto/toggle", { enabled: nextEnabled });
      setAutoStatus(data);
      if (nextEnabled) {
        toast.success("Always-on loop ENABLED · running 24/7 until every site is done");
      } else {
        toast.info("Always-on loop paused");
      }
    } catch (e) {
      toast.error("Could not update the loop");
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enrich-facts/run?limit=${batchSize}`);
      if (!data.ok) {
        toast.error(data.reason || "Could not start run");
      } else {
        toast.success(`Started batch of ${data.queued} sites — Claude is reading them now…`);
        load();
      }
    } catch (e) {
      toast.error("Could not start the run");
    } finally {
      setBusy(false);
    }
  };

  const runUntilDone = async () => {
    if (!window.confirm(
      `This will run Claude across every remaining unenriched site in the catalogue (currently ${needed?.toLocaleString() || "?"}).\n\n` +
      `Estimated cost: £${((needed || 0) * 0.001).toFixed(2)} · ` +
      `Estimated time: ${Math.ceil((needed || 0) * 3 / 60)} minutes.\n\n` +
      `The job runs in the background — you can close this page.\n\nContinue?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enrich-facts/run-until-done?batch_size=100&max_total=6000`);
      if (!data.ok) {
        toast.error(data.reason || "Could not start the loop");
      } else {
        toast.success(`One-shot loop started · run_id ${data.run_id}. Polling for progress…`);
        load();
      }
    } catch (e) {
      toast.error("Could not start the loop");
    } finally {
      setBusy(false);
    }
  };

  const isRunning = lastRun && lastRun.status === "running";
  const isStale = lastRun && lastRun.status === "stale";
  const autoOn = Boolean(autoStatus && autoStatus.enabled);
  const autoRunning = Boolean(autoStatus && autoStatus.running);

  return (
    <section className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white" data-testid="admin-enrich-facts-card">
      <header className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3" /> AI catalogue enrichment
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">Fill in the filter gaps with Claude</h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            Most of the 22k catalogue has <code className="bg-[#f5f5f5] px-1 rounded">dog_policy=unknown</code> /
            no family/pitch-surface/campfire data. This sends each site&apos;s description through Claude Sonnet 4.5
            and extracts those structured fields. ≈ £0.001 per site · safe to stop any time.
          </p>
        </div>
        <button onClick={load} className="text-xs text-[#666] hover:text-[#ff4500] inline-flex items-center gap-1" data-testid="enrich-facts-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </header>

      {/* Always-on loop panel */}
      <div className={`mb-4 p-4 rounded-xl border-2 ${autoOn ? "bg-[#fff7ee] border-[#ff4500]" : "bg-[#fafafa] border-[#e5e5e5]"}`} data-testid="enrich-facts-auto-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 mb-1">
              <Zap className={`w-4 h-4 ${autoOn ? "text-[#ff4500]" : "text-[#999]"}`} />
              <span className="font-display font-bold text-base">Always-on loop</span>
              {autoOn && autoRunning && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#1f7a35] bg-[#e6f4ea] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1f7a35] animate-pulse" /> LIVE
                </span>
              )}
              {autoOn && !autoRunning && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  Booting…
                </span>
              )}
            </div>
            <p className="text-xs text-[#666]">
              Runs 24/7 in the background · survives server restarts · keeps grinding until every site is done.
              {autoStatus && !autoStatus.api_key_configured && (
                <span className="block text-amber-700 mt-1">⚠ EMERGENT_LLM_KEY not set — enable will do nothing.</span>
              )}
            </p>
          </div>
          <button
            onClick={() => toggleAuto(!autoOn)}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors disabled:opacity-50 ${autoOn ? "bg-[#666] hover:bg-[#333]" : "bg-[#ff4500] hover:bg-[#e63e00]"}`}
            data-testid="enrich-facts-auto-toggle"
          >
            {autoOn ? <><PauseCircle className="w-3 h-3" /> Pause loop</> : <><Zap className="w-3 h-3" /> Turn on 24/7</>}
          </button>
        </div>
        {autoStatus && (
          <div className="grid sm:grid-cols-4 gap-2 mt-3">
            <MiniStat label="Session · processed" value={(autoStatus.session_processed || 0).toLocaleString()} />
            <MiniStat label="Session · facts written" value={(autoStatus.session_updated || 0).toLocaleString()} />
            <MiniStat label="Remaining" value={(autoStatus.remaining || 0).toLocaleString()} />
            <MiniStat label="ETA" value={autoStatus.eta_hours > 1 ? `${autoStatus.eta_hours}h` : `${autoStatus.eta_minutes || 0}m`} />
          </div>
        )}
        {autoStatus && autoStatus.last_error && (
          <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mono">
            Last error: {autoStatus.last_error}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Sites needing enrichment" value={needed === null ? "…" : needed.toLocaleString()} />
        <Stat label="Last manual run · processed" value={lastRun?.processed ?? "—"} />
        <Stat label="Last manual run · facts written" value={lastRun?.updated ?? "—"} />
      </div>

      {lastRun && (
        <div className="mb-4 p-3 bg-[#fafafa] border hairline rounded-xl text-xs mono">
          <div className="text-[#666] uppercase tracking-wider mb-1">Last manual run</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            <span><strong>{lastRun.run_id}</strong></span>
            <span>· status: <strong className={lastRun.status === "done" ? "text-[#1f7a35]" : (isStale ? "text-amber-700" : "text-[#ff4500]")}>{lastRun.status}</strong></span>
            <span>· queued {lastRun.queued}</span>
            <span>· processed {lastRun.processed}</span>
            <span>· updated {lastRun.updated}</span>
            {lastRun.errors > 0 && <span className="text-amber-700">· errors {lastRun.errors}</span>}
          </div>
          {isStale && (
            <div className="mt-2 text-amber-800">
              ⚠ This run was auto-marked stale — the server restarted while it was in flight. Turn on the always-on loop above so restarts stop killing progress.
            </div>
          )}
        </div>
      )}

      <details className="mb-3">
        <summary className="text-xs text-[#666] cursor-pointer hover:text-[#ff4500]">Advanced · manual one-off batch</summary>
        <div className="flex items-end gap-3 flex-wrap mt-3 p-3 bg-[#fafafa] border hairline rounded-xl">
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">Batch size</div>
            <input
              type="number" min="1" max="500" value={batchSize}
              onChange={(e) => setBatchSize(Math.max(1, Math.min(500, parseInt(e.target.value || "25", 10))))}
              className="w-24 px-3 py-2 text-sm border border-[#e5e5e5] rounded-lg focus:outline-none focus:border-[#ff4500]"
              data-testid="enrich-facts-batch-size"
            />
          </label>
          <button
            onClick={run}
            disabled={busy || isRunning || needed === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors"
            data-testid="enrich-facts-run"
          >
            {isRunning ? <><RefreshCw className="w-3 h-3 animate-spin" /> Running…</> : <><Play className="w-3 h-3" /> Run batch ({batchSize})</>}
          </button>
          <button
            onClick={runUntilDone}
            disabled={busy || isRunning || needed === 0 || needed === null}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1f7a35] hover:bg-[#176028] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors"
            data-testid="enrich-facts-run-until-done"
            title="One-shot loop — dies on server restart (use always-on toggle instead for durability)"
          >
            <Sparkles className="w-3 h-3" /> Run until done
          </button>
          {needed === 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-[#1f7a35]">
              <Check className="w-3 h-3" /> Nothing left to enrich
            </span>
          )}
          {needed && needed > 1000 && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
              <AlertTriangle className="w-3 h-3" /> Full run ≈ £{(needed * 0.001).toFixed(2)}
            </span>
          )}
        </div>
      </details>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#fafafa] border hairline rounded-xl p-3">
      <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">{label}</div>
      <div className="font-display text-xl font-bold text-[#0e0e0e]">{value}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white border border-[#f0f0f0] rounded-lg p-2">
      <div className="mono text-[9px] uppercase tracking-wider text-[#666]">{label}</div>
      <div className="font-display text-sm font-bold text-[#0e0e0e]">{value}</div>
    </div>
  );
}
